/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	Image,
	Box,
	Drawer as NativeDrawer,
	Text,
	Button,
	DrawerProps,
	Tooltip,
} from '@mantine/core';

// Utils
import { truncateString } from '@utils/functions';

// style
import classes from './DrawerApiToken.module.css';

// icons
import { IconTrash } from '@tabler/icons-react';
import ApiTokenLogo from '@assets/images/api-token-logo.svg';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { TApiToken } from '../../types';

// lodash
import isNil from 'lodash/isNil';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerApiToken = (
	props: DrawerProps & {
		onClose: () => void;
		handledelete?: (
			arg: React.MouseEvent<HTMLElement>
		) => void;
		opened: boolean;
		data: Partial<TApiToken>;
	}
) => {
	const {
		opened,
		onClose,
		handledelete,
		position,
		...restProps
	} = props;

	/* ******************************** I18N ******************************* */
	const { t } = useTranslation();

	const { checkAcl } = useAuthorization();
	/* ******************************** RENDERING ******************************* */

	if (!isNil(props.data))
		return (
			<NativeDrawer
				onClose={onClose}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				position={position}
				{...restProps}
			>
				{/* Header */}
				<Box className={classes.customHeader}>
					<Box className={classes.containerLogo}>
						<Image
							className={classes.logo}
							src={ApiTokenLogo}
						/>
					</Box>

					<Box className={classes.containerTitle}>
						<Text className={classes.customSubTitle}>
							{t('global.apiToken')}
						</Text>
						{props.data?.name && (
							<Tooltip
								label={props.data.name}
								disabled={props.data.name.length <= 20}
							>
								<Text className={classes.customTitle}>
									{truncateString(props.data.name, 20)}
								</Text>
							</Tooltip>
						)}
					</Box>
				</Box>

				{/* Body */}
				<Box className={classes.bodyDrawer}>
					{props.data?.organization && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.organization')}
							</Text>
							<Tooltip
								label={props.data.organization}
								disabled={
									props.data.organization.length <= 20
								}
							>
								<Text className={classes.value}>
									{truncateString(
										props.data.organization,
										20
									)}
								</Text>
							</Tooltip>
						</Box>
					)}
					{!isNil(props.data.producer) && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.producer')}
							</Text>
							<Tooltip
								label={props.data.producer}
								disabled={
									props.data.producer.length <= 20
								}
							>
								<Text className={classes.value}>
									{truncateString(
										props.data.producer,
										20
									)}
								</Text>
							</Tooltip>
						</Box>
					)}
					{props.data?.createdAt && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.creationDate')}
							</Text>
							<Text className={classes.value}>
								{props.data.createdAt}
							</Text>
						</Box>
					)}
					{props.data.createdAt && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.last8chars')}
							</Text>
							<Text className={classes.value}>
								{props.data.last8chars}
							</Text>
						</Box>
					)}
					{props.data?.expiredAt && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.expirationDate')}
							</Text>
							<Text className={classes.value}>
								{props.data.expiredAt}
							</Text>
						</Box>
					)}
					{props.data?.updatedAt && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.lastUsedDate')}
							</Text>
							<Text className={classes.value}>
								{props.data.updatedAt}
							</Text>
						</Box>
					)}
					{props.data?.domain && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.domain')}
							</Text>
							<Tooltip
								label={props.data.domain}
								disabled={props.data.domain.length <= 25}
							>
								<Text className={classes.value}>
									{truncateString(props.data?.domain, 25)}
								</Text>
							</Tooltip>
						</Box>
					)}
				</Box>

				{/* Footer */}
				{checkAcl(ACLS.API_TOKENS.DELETE) && (
					<Box className={classes.footer}>
						<Box className={classes.footerSpacer} />
						<Button
							onClick={handledelete}
							leftSection={<IconTrash size={16} />}
							hiddenFrom="xxl"
							size="sm"
							fullWidth
							classNames={{
								root: classes.customDeleteButton,
							}}
						>
							{t('apiTokens.deleteApiToken')}
						</Button>
						<Button
							onClick={handledelete}
							leftSection={<IconTrash size={20} />}
							size="md"
							visibleFrom="xxl"
							fullWidth
							classNames={{
								root: classes.customDeleteButton,
							}}
						>
							{t('apiTokens.deleteApiToken')}
						</Button>
					</Box>
				)}
			</NativeDrawer>
		);
};
